import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { formatAge, getPoolOverview, type PoolOverview } from "@admin/lib/pool";
import { Card, CardContent, CardHeader, CardTitle } from "@admin/components/ui/card";

const mb = (v: number | null) => (v == null ? "-" : `${v} MB`);

export default function PoolPage() {
  const [data, setData] = useState<PoolOverview | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    getPoolOverview()
      .then(setData)
      .catch(() => setErr("Could not load the pool"));
  }, []);

  if (err) return <p className="text-danger">{err}</p>;
  if (!data) return <p className="text-muted-fg">Loading...</p>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Pool</h1>
        <p className="mt-1 text-sm text-muted-fg">
          Container-tier workers and their live allocations (read only). A worker whose heartbeat is older than 3
          minutes is reported in fleet health.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Workers ({data.workers.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {data.workers.length === 0 ? (
            <p className="text-sm text-muted-fg">No workers registered.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-muted-fg">
                    <th className="pb-2 pr-4 font-medium">Name</th>
                    <th className="pb-2 pr-4 font-medium">Status</th>
                    <th className="pb-2 pr-4 font-medium">Metro</th>
                    <th className="pb-2 pr-4 font-medium">Memory (reserved / total)</th>
                    <th className="pb-2 pr-4 font-medium">Available</th>
                    <th className="pb-2 pr-4 font-medium">Swap (used / total)</th>
                    <th className="pb-2 pr-4 font-medium">Disk free</th>
                    <th className="pb-2 pr-4 font-medium">Slots</th>
                    <th className="pb-2 pr-4 font-medium">Heartbeat</th>
                    <th className="pb-2 font-medium">Preflight</th>
                  </tr>
                </thead>
                <tbody>
                  {data.workers.map((w) => (
                    <tr key={w.id} className="border-b border-border/60">
                      <td className="py-2 pr-4 font-mono text-xs">{w.name}</td>
                      <td className="py-2 pr-4">{w.status}</td>
                      <td className="py-2 pr-4">{w.metro}</td>
                      <td className="py-2 pr-4 tabular-nums">
                        {w.memReservedMb} / {w.memTotalMb} MB
                      </td>
                      <td className="py-2 pr-4 tabular-nums">{mb(w.memAvailableMb)}</td>
                      <td className="py-2 pr-4 tabular-nums">
                        {w.swapUsedMb ?? "-"} / {w.swapTotalMb} MB
                      </td>
                      <td className="py-2 pr-4 tabular-nums">{mb(w.diskFreeMb)}</td>
                      <td className="py-2 pr-4 tabular-nums">
                        {w.slotsReserved} / {w.maxSlots}
                      </td>
                      <td className={`py-2 pr-4 tabular-nums ${w.heartbeatStale ? "text-danger" : ""}`}>
                        {formatAge(w.heartbeatAgeSec)}
                        {w.heartbeatStale ? " (stale)" : ""}
                      </td>
                      <td className={`py-2 ${w.preflight === "failed" ? "text-danger" : ""}`}>{w.preflight}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Active allocations ({data.allocations.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {data.allocations.length === 0 ? (
            <p className="text-sm text-muted-fg">No active allocations.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-muted-fg">
                    <th className="pb-2 pr-4 font-medium">Worker</th>
                    <th className="pb-2 pr-4 font-medium">State</th>
                    <th className="pb-2 pr-4 font-medium">User</th>
                    <th className="pb-2 font-medium">Server</th>
                  </tr>
                </thead>
                <tbody>
                  {data.allocations.map((a) => (
                    <tr key={a.serverId} className="border-b border-border/60">
                      <td className="py-2 pr-4 font-mono text-xs">{a.workerName}</td>
                      <td className="py-2 pr-4">{a.state}</td>
                      <td className="py-2 pr-4">
                        <Link to={`/users/${a.userId}`} className="font-mono text-xs text-accent hover:underline">
                          {a.userId}
                        </Link>
                      </td>
                      <td className="py-2 font-mono text-xs">{a.serverId}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
